import React from "react";
import { Link } from "react-router-dom";
import { Zap, Activity, Bone, ShieldCheck, ArrowRight, Trophy, ExternalLink, Clock, Award, Waves, CheckCircle2, BookOpen } from "lucide-react";
import { ATHLETIC_BENEFITS, SPOTLIGHT_REFERENCES, EDGE_PROTOCOLS, EDGE_STATS } from "../mock";

const ICONS = { Zap, Activity, Bone, ShieldCheck };

export default function TheEdgePage() {
  return (
    <div className="bg-[#f4ede0] text-[#1a2a38]">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211"
          alt="Athlete training at sunrise"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c44]/85 via-[#0f2c44]/65 to-[#0f2c44]/85" />
        <div className="relative max-w-5xl mx-auto px-6 text-center text-[#f4ede0] py-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#d4a857]/60 rounded-full mb-6">
            <Trophy size={14} className="text-[#d4a857]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#d4a857]">The Edge</span>
          </div>
          <h1
            className="text-5xl md:text-7xl lg:text-8xl leading-[1.02] font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Performance,<br />
            <span className="italic text-[#d4a857]">naturally amplified.</span>
          </h1>
          <p className="mt-8 text-xl md:text-2xl text-[#f4ede0]/90 font-light max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            The daily ritual professional athletes have quietly adopted to recover faster, train longer, and compete cleaner.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link
              to="/waitlist"
              className="inline-flex items-center gap-2 bg-[#d4a857] hover:bg-[#b8893d] text-[#0f2c44] px-8 py-3.5 rounded-sm text-lg transition-colors font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Join the waitlist <ArrowRight size={18} />
            </Link>
            <a
              href="#testimonials"
              className="inline-flex items-center gap-2 border border-[#f4ede0]/60 hover:bg-[#f4ede0]/10 text-[#f4ede0] px-8 py-3.5 rounded-sm text-lg transition-colors"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              See who's already on it
            </a>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#1e3a52] text-[#f4ede0]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {EDGE_STATS.map((s, i) => (
            <div key={i} className="border-r last:border-r-0 md:border-r border-[#f4ede0]/15 px-4">
              <div className="text-4xl md:text-5xl text-[#d4a857]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.value}</div>
              <div className="mt-3 text-sm text-[#f4ede0]/85 leading-relaxed">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY POLYPHENOLS, WHY NOW */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">Why polyphenols, why now</p>
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            High-polyphenol olive oil is moving from <span className="italic text-[#4a7ba1]">Mediterranean kitchen to performance protocol.</span>
          </h2>
          <p className="mt-6 text-[#3a4a58] leading-relaxed">
            Longevity researchers, ultra-endurance athletes, and the largest clinical-nutrition trials ever conducted all converge on the same input: a daily dose of high-phenolic extra virgin olive oil. FOYEVOO is built around that exact dose &mdash; sealed at peak potency.
          </p>
        </div>
      </section>

      {/* PERFORMANCE BENEFITS */}
      <section className="bg-[#ebe2cd]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">The edge, explained</p>
            <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Four mechanisms. One daily pod.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {ATHLETIC_BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon] || Zap;
              return (
                <div key={i} className="bg-[#f4ede0] border border-[#4a7ba1]/15 rounded-sm p-8 hover:shadow-lg hover:border-[#d4a857]/40 transition-all">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-sm bg-[#1e3a52] grid place-items-center flex-shrink-0">
                      <Icon size={26} className="text-[#d4a857]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-2xl text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{b.title}</h3>
                      <p className="mt-3 text-[#3a4a58] leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPOTLIGHT: REAL PUBLIC REFERENCES */}
      <section id="testimonials" className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">In the spotlight</p>
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            High-polyphenol olive oil, on the record.
          </h2>
          <p className="mt-5 text-sm text-[#3a4a58]">Publicly documented references only &mdash; no fabricated endorsements.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {SPOTLIGHT_REFERENCES.map((s, i) => (
            <article key={i} className="bg-[#f4ede0] border border-[#4a7ba1]/15 rounded-sm p-7 hover:shadow-md hover:border-[#d4a857]/40 transition-all flex flex-col">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-2xl text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.name}</h3>
                  <div className="text-sm text-[#4a7ba1] mt-1">{s.role}</div>
                </div>
                <BookOpen size={22} className="text-[#d4a857] flex-shrink-0" strokeWidth={1.5} />
              </div>
              <p className="mt-3 text-xs uppercase tracking-widest text-[#3a4a58]/70">{s.context}</p>
              <p className="mt-5 text-[#1a2a38] leading-relaxed flex-1">{s.summary}</p>
              <a
                href={s.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#1e3a52] hover:text-[#d4a857] border-b border-[#d4a857]/40 hover:border-[#d4a857] pb-0.5 self-start"
              >
                {s.sourceLabel} <ExternalLink size={13} />
              </a>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center max-w-2xl mx-auto">
          <p className="text-sm text-[#3a4a58] italic">
            Customer and athlete testimonials will be published here once collected with full written consent. Until then, we'll only cite what's already on the public record.
          </p>
        </div>
      </section>

      {/* ACTION SHOT BAND */}
      <section className="relative">
        <img
          src="https://images.unsplash.com/photo-1545151414-8a948e1ea54f"
          alt="Professional sport in action"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2c44]/95 via-[#0f2c44]/70 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-28">
          <div className="max-w-2xl text-[#f4ede0]">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#d4a857]/60 rounded-full mb-5">
              <Waves size={14} className="text-[#d4a857]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#d4a857]">100% natural</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              No banned substances. No grey areas. Just olives, sealed at peak potency.
            </h2>
            <p className="mt-6 text-[#f4ede0]/85 leading-relaxed text-lg">
              Every batch is third-party tested at IOC-accredited laboratories. The only thing in the pod is cold-extracted extra virgin olive oil from a single early harvest. That's it.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "WADA-clean &mdash; no banned compounds, ever",
                "Lab-verified polyphenol load on every batch",
                "Nitrogen-sealed monodose &mdash; stable in your kit bag",
                "Compatible with any clean-sport supplement stack",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#d4a857] mt-1 flex-shrink-0" strokeWidth={2} />
                  <span dangerouslySetInnerHTML={{ __html: t }} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROTOCOL */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">The athlete's protocol</p>
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            A year-round rhythm, not a quick fix.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {EDGE_PROTOCOLS.map((p, i) => (
            <div key={i} className="relative p-7 bg-[#1e3a52] text-[#f4ede0] rounded-sm overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#d4a857]/15 rounded-full -translate-y-8 translate-x-8" />
              <Clock size={22} className="text-[#d4a857]" strokeWidth={1.5} />
              <h3 className="mt-4 text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.phase}</h3>
              <p className="mt-3 text-sm text-[#f4ede0]/80 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCIENCE CALLOUT */}
      <section className="bg-[#ebe2cd]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">The science layer</p>
            <h3 className="text-3xl md:text-4xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Why polyphenols outperform conventional anti-inflammatories for daily training.
            </h3>
            <p className="mt-5 text-[#3a4a58] leading-relaxed">
              Oleocanthal, the active compound that gives high-phenolic olive oil its peppery throat-tingle, inhibits the same COX-1 and COX-2 enzymes as ibuprofen &mdash; but without the gastrointestinal toxicity of chronic NSAID use. For athletes managing daily inflammation across long seasons, that distinction matters.
            </p>
            <p className="mt-4 text-[#3a4a58] leading-relaxed">
              Hydroxytyrosol &mdash; the most potent natural antioxidant in olive oil &mdash; supports mitochondrial function and helps protect cellular structures against the oxidative load generated by heavy training blocks.
            </p>
            <Link
              to="/health-benefits"
              className="inline-flex items-center gap-2 mt-6 text-[#1e3a52] border-b border-[#d4a857] hover:text-[#d4a857] pb-1"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
            >
              Read the full clinical evidence <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/9602280/pexels-photo-9602280.jpeg"
              alt="Athletic recovery"
              className="w-full h-[480px] object-cover rounded-sm shadow-xl"
            />
            <div className="absolute -bottom-5 -right-5 bg-[#d4a857] text-[#0f2c44] px-5 py-3 rounded-sm shadow-lg hidden md:flex items-center gap-2">
              <Award size={18} />
              <span className="text-sm uppercase tracking-widest font-medium">Clean-sport approved</span>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1439405326854-014607f694d7"
          alt="Ocean sunrise"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0f2c44]/80" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center text-[#f4ede0]">
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Find your edge. <span className="italic text-[#d4a857]">Sealed at peak potency.</span>
          </h2>
          <p className="mt-6 text-[#f4ede0]/85 leading-relaxed">
            Start the same daily ritual quietly adopted by professional athletes in some of the world's most demanding leagues.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/waitlist" className="bg-[#d4a857] hover:bg-[#b8893d] text-[#0f2c44] px-8 py-3.5 rounded-sm text-lg transition-colors font-medium" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Reserve the 2025 Harvest
            </Link>
            <Link to="/waitlist" className="border border-[#f4ede0]/60 hover:bg-[#f4ede0]/10 text-[#f4ede0] px-8 py-3.5 rounded-sm text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Join the waitlist
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
